import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import '../styles/CareersSection.css';

const CareersSection = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const copyEmailToClipboard = () => {
    navigator.clipboard.writeText('hola@runamatic.io').then(() => {
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    });
  };

  const jobOpenings = [
    {
      id: 1,
      title: "Chatbot Developer",
      location: "Argentina - Remoto",
      type: "Pago por Proyecto",
      category: "Desarrollo",
      description: "Buscamos personas de Argentina con capacidades técnicas, autosuficientes y con ganas de crecer dentro del proyecto. Tu rol será configurar chatbots a medida para distintos rubros, creando soluciones innovadoras que mejoren la experiencia del cliente.",
      requirements: [
        "Experiencia en desarrollo de chatbots y automatización",
        "Conocimientos en plataformas de messaging (WhatsApp, Telegram, etc.)",
        "Capacidad de trabajo autónomo y proactivo",
        "Habilidades de resolución de problemas",
        "Mentalidad de crecimiento y aprendizaje continuo"
      ],
      techRequirements: [
        "Buena conexión a internet estable",
        "Excelente imagen y presencia profesional",
        "Buen nivel de comunicación oral y escrita",
        "Notebook propia con micrófono y cámara",
        "Disponibilidad horaria flexible"
      ],
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      buttonText: "¿Te apasiona la tecnología? Esperamos tu CV",
      buttonTextMobile: "¿Te gusta programar? Envía tu CV",
      buttonIcon: "fas fa-code"
    },
    {
      id: 2,
      title: "Trafficker / Content Creator",
      location: "Argentina - Remoto",
      type: "Pago por Proyecto",
      category: "Marketing",
      description: "Personas de Argentina con conocimientos en publicidad digital y creación de contenido. Tu rol será generar tráfico hacia canales de venta mediante estrategias creativas e innovadoras en múltiples plataformas.",
      requirements: [
        "Experiencia comprobable en Meta Ads (Facebook/Instagram)",
        "Conocimientos en Google Ads y YouTube Ads",
        "Experiencia en TikTok Ads y marketing en redes sociales",
        "Habilidades de copywriting y storytelling",
        "Análisis de métricas y optimización de campañas",
        "Creatividad para generar contenido atractivo"
      ],
      techRequirements: [
        "Buena conexión a internet estable",
        "Excelente imagen y presencia profesional",
        "Buen nivel de comunicación oral y escrita",
        "Notebook propia con micrófono y cámara",
        "Software de diseño básico (Canva, Photoshop, etc.)"
      ],
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      buttonText: "¿Eres creativo y analítico? Te esperamos",
      buttonTextMobile: "¿Eres creativo? Envía tu CV",
      buttonIcon: "fas fa-bullhorn"
    },
    {
      id: 3,
      title: "Especialista en Ventas B2B",
      location: "Argentina - Remoto",
      type: "Pago por Proyecto",
      category: "Ventas",
      description: "Personas de Argentina con experiencia sólida en ventas B2B. Tu rol será desarrollar y mantener relaciones comerciales con empresas, identificando oportunidades y cerrando acuerdos que impulsen el crecimiento de Runamatic.",
      requirements: [
        "Experiencia demostrable en ventas B2B (mínimo 2 años)",
        "Conocimiento del ciclo de ventas complejo",
        "Habilidades de negociación y cierre",
        "Experiencia con CRM y herramientas de ventas",
        "Capacidad de identificar necesidades del cliente",
        "Orientación a resultados y metas"
      ],
      techRequirements: [
        "Buena conexión a internet estable",
        "Excelente imagen y presencia profesional",
        "Buen nivel de comunicación oral y escrita",
        "Notebook propia con micrófono y cámara",
        "Disponibilidad para reuniones con clientes"
      ],
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      buttonText: "¿Te apasionan las ventas? Esperamos tu CV",
      buttonTextMobile: "¿Te gustan las ventas? Envía tu CV",
      buttonIcon: "fas fa-handshake"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div className="careers-section">
      {/* Background Elements */}
      <div className="careers-background">
        <div className="careers-shape careers-shape-1"></div>
        <div className="careers-shape careers-shape-2"></div>
        <div className="careers-shape careers-shape-3"></div>
        <div className="floating-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className={`particle particle-${i + 1}`}></div>
          ))}
        </div>
      </div>

      <div className="careers-container">
        {/* Header Section */}
        <motion.div 
          className="careers-header"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div className="careers-hero" variants={itemVariants}>
            <h1 className="careers-title">
              <span className="title-gradient">Carreras en</span>
              <br />
              <span className="title-brand">Runamatic</span>
            </h1>
            <p className="careers-subtitle">
              Únete a nuestro equipo y forma parte de la revolución en automatización 
              de atención al cliente. Construimos el futuro de las conversaciones inteligentes.
            </p>
          </motion.div>

          <motion.div className="careers-stats" variants={itemVariants}>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Remoto</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">+3</div>
              <div className="stat-label">Posiciones Abiertas</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">🇦🇷</div>
              <div className="stat-label">Argentina</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Job Openings */}
        <motion.div 
          className="job-openings"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Oportunidades Disponibles
          </motion.h2>

          <div className="jobs-grid">
            {jobOpenings.map((job, index) => (
              <motion.div
                key={job.id}
                className="job-card"
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div 
                  className="job-card-header"
                  style={{ background: job.gradient }}
                >
                  <div className="job-category">{job.category}</div>
                  <h3 className="job-title">{job.title}</h3>
                  <div className="job-meta">
                    <span className="job-location">
                      <i className="fas fa-map-marker-alt"></i>
                      {job.location}
                    </span>
                    <span className="job-type">
                      <i className="fas fa-clock"></i>
                      {job.type}
                    </span>
                  </div>
                </div>

                <div className="job-card-body">
                  <p className="job-description">{job.description}</p>

                  <div className="job-requirements">
                    <h4>Requisitos Profesionales:</h4>
                    <ul>
                      {job.requirements.map((req, reqIndex) => (
                        <li key={reqIndex}>{req}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="job-tech-requirements">
                    <h4>Requisitos Técnicos:</h4>
                    <ul>
                      {job.techRequirements.map((req, reqIndex) => (
                        <li key={reqIndex}>{req}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="job-card-footer">
                  <motion.button
                    className="apply-button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={copyEmailToClipboard}
                  >
                    <i className={job.buttonIcon}></i>
                    {isMobile ? job.buttonTextMobile : job.buttonText}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Application Section */}
        <motion.div 
          className="application-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.div className="application-card" variants={itemVariants}>
            <div className="application-icon">
              <i className="fas fa-envelope"></i>
            </div>
            <h3>¿Listo para unirte?</h3>
            <p>
              Envía tu CV y una carta de presentación contándonos por qué 
              quieres formar parte del equipo Runamatic.
            </p>
            <motion.button
              className={`email-button ${copiedEmail ? 'copied' : ''}`}
              onClick={copyEmailToClipboard}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <i className={`fas ${copiedEmail ? 'fa-check' : 'fa-copy'}`}></i>
              {copiedEmail ? 'Email copiado!' : 'hola@runamatic.io'}
            </motion.button>
            <div className="application-note">
              Haz clic en el email para copiarlo automáticamente
            </div>
          </motion.div>
        </motion.div>

        {/* Company Culture */}
        <motion.div 
          className="culture-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            ¿Por qué Runamatic?
          </motion.h2>
          
          <div className="culture-grid">
            <motion.div className="culture-item" variants={itemVariants}>
              <div className="culture-icon">🚀</div>
              <h4>Crecimiento Acelerado</h4>
              <p>Oportunidades reales de desarrollo profesional y personal en una empresa en rápido crecimiento.</p>
            </motion.div>
            
            <motion.div className="culture-item" variants={itemVariants}>
              <div className="culture-icon">🌎</div>
              <h4>Trabajo Remoto</h4>
              <p>Flexibilidad total para trabajar desde donde te sientas más cómodo y productivo.</p>
            </motion.div>
            
            <motion.div className="culture-item" variants={itemVariants}>
              <div className="culture-icon">💡</div>
              <h4>Innovación Constante</h4>
              <p>Trabajamos con las últimas tecnologías y siempre buscamos formas de mejorar y crecer.</p>
            </motion.div>
            
            <motion.div className="culture-item" variants={itemVariants}>
              <div className="culture-icon">🤝</div>
              <h4>Equipo Colaborativo</h4>
              <p>Un ambiente de trabajo donde cada voz cuenta y la colaboración es fundamental.</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CareersSection; 